import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { Model } from "@/lib/Model.ts"

import type { CodingGuidelinesModel as ICodingGuidelinesModel } from "@/components/CodingGuidelinesDialog/CodingGuidelinesModel.ts"
import type { PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"


export class CodingGuidelinesDialogModel extends Model implements ICodingGuidelinesModel {
  readonly initialGuidelines = observable.box("")
  readonly guidelines = observable.box("")
  readonly readonly = observable.box(false)

  readonly hasChanges = computed(() => this.guidelines.get() !== this.initialGuidelines.get())
  readonly isSaving = observable.box(false)

  readonly handleSave = () => {
    if (this.readonly.get()) {
      toast.error("Project guidelines are read-only")
      return
    }
    runInAction(() => {
      this.isSaving.set(true)
    })
    try {
      void this.queries.apiClient.updateProjectCodingGuidelines(
        this.project.key,
        this.project.repository.name,
        this.guidelines.get()
      ).then(() => {
        runInAction(() => this.initialGuidelines.set(this.guidelines.get()))
        toast.success("Guidelines updated")
      })
    } finally {
      runInAction(() => {
        this.isSaving.set(false)
      })
    }
  }

  readonly handleDiscard = () => {
    runInAction(() => this.guidelines.set(this.initialGuidelines.get()))
  }

  readonly load = async () => {
    runInAction(() => this.isLoading.set(true))

    try {
      const content = await this.queries.getProjectCodingGuidelines(this.project.key, this.project.repository.name)

      runInAction(() => {
        const value = content?.content ?? ""
        this.initialGuidelines.set(value)
        this.guidelines.set(value)
        this.readonly.set(content?.readonly ?? false)
      })
    } finally {
      runInAction(() => this.isLoading.set(false))
    }
  }

  constructor(private readonly project: PR_Project2, readonly queries: Queries) {
    super("CodingGuidelinesDialogModel")

    void this.load()
  }
}

